// ═══════════════════════════════════════════════════════════════
// LA CAPITAL — Main Application
// Data-driven, modular, premium
// ═══════════════════════════════════════════════════════════════

import { menuItems, categories, getMenuByCategory, formatPrice } from './data/menu.js'
import { events, eventCategories, getEventsByCategory } from './data/events.js'
import { brandAssets } from './data/brandAssets.js'
import { promotions } from './data/promotions.js'
import { testimonials } from './data/testimonials.js'
import { faq } from './data/faq.js'
// Three.js modules loaded lazily for code splitting
let burgerBuilderApi = null
async function loadThreeModules() {
  const hero = await import('./three/burger-hero.js')
  // The module auto-inits on DOMContentLoaded which already fired,
  // so manually trigger if the function is exported
  if (hero.initBurgerHero) hero.initBurgerHero()
  const builder = await import('./three/burger-builder.js')
  burgerBuilderApi = builder
}
import { setSize, toggleTopping, getBurgerPrice, getState, onBuilderChange } from './logic/builder-state.js'

// ── STATE ──
const state = {
  cart: JSON.parse(localStorage.getItem('lc_cart') || '[]'),
  activeCategory: 'hamburguesas',
  activeEventTab: 'all',
  mobileOpen: false,
  cartOpen: false,
  builderSize: 'S',
  builderToppings: [],
  builderPrices: { S: 220, M: 350, L: 480 },
  toppingPrices: { cheddar: 50, bacon: 80, mushrooms: 65, jalapenos: 40 }
}

// ── INIT ──
document.addEventListener('DOMContentLoaded', () => {
  renderMenu()
  renderBrandGallery()
  renderMenuPdfPreview()
  renderEvents()
  renderTestimonials()
  renderFAQ()
  initCart()
  initBuilder()
  initHeader()
  initMobileNav()
  initReservationForm()
  initEventTabs()
  initMenuFilters()
  initSmoothScroll()
  setMinDate()
})

// ═══════════════════════════════════════════════════════════════
// MENU RENDERING
// ═══════════════════════════════════════════════════════════════

function renderMenu(categoryId = state.activeCategory) {
  const grid = document.getElementById('menu-grid')
  if (!grid) return

  const items = getMenuByCategory(categoryId)
  grid.innerHTML = items.map(item => `
    <article class="menu-card" data-id="${item.id}">
      ${item.image ? `<div class="card-img" style="background-image: url('${resolveMenuImage(item.image)}')" role="img" aria-label="${item.name}"></div>` : ''}
      <div class="card-body">
        <h3 class="card-title">${item.name}</h3>
        ${item.tags?.length ? `<div class="card-tags">${item.tags.map(t => `<span class="card-tag${t === 'spicy' ? ' card-tag--spicy' : ''}">${t}</span>`).join('')}</div>` : ''}
        ${item.description ? `<p class="card-desc">${item.description}</p>` : ''}
        <div class="card-footer">
          <span class="card-price">${formatPrice(item)}</span>
          ${item.price ? `<button class="add-btn" data-id="${item.id}" data-name="${item.name}" data-price="${item.price}" aria-label="Añadir ${item.name} al carrito">+</button>` : ''}
        </div>
      </div>
    </article>
  `).join('')

  // Animate cards in
  requestAnimationFrame(() => {
    grid.querySelectorAll('.menu-card').forEach((card, i) => {
      card.style.opacity = '0'
      card.style.transform = 'translateY(20px)'
      setTimeout(() => {
        card.style.transition = 'all 0.5s cubic-bezier(0.25, 1, 0.25, 1)'
        card.style.opacity = '1'
        card.style.transform = 'translateY(0)'
      }, i * 60)
    })
  })

  // Bind add buttons
  grid.querySelectorAll('.add-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      addToCart({
        id: btn.dataset.id,
        name: btn.dataset.name,
        price: parseInt(btn.dataset.price)
      })
    })
  })
}

function resolveMenuImage(image) {
  if (!image) return ''
  if (image.startsWith('/')) return image
  return `/src/assets/images/${image}.jpg`
}

function renderBrandGallery() {
  const grid = document.getElementById('brand-gallery-grid')
  if (!grid) return
  const priority = [
    'burger_capital_mermelada_real',
    'burger_bacon_real',
    'burger_buffalo_real',
    'burger_mushrooms_real',
    'nachos_birria_real',
    'quesibirria_real',
    'canasta_birria_real',
    'nachos_buffalo_table_real'
  ]
  const assets = priority
    .map(slug => brandAssets.food.find(item => item.slug === slug))
    .filter(Boolean)

  grid.innerHTML = assets.map((asset, index) => `
    <figure class="brand-shot brand-shot--${index % 4}">
      <img src="${asset.paths.card}" alt="Foto real de La Capital Gastro Bar: ${asset.slug.replaceAll('_', ' ')}" loading="lazy">
      <figcaption>${asset.slug.replaceAll('_', ' ').replace('real', '').trim()}</figcaption>
    </figure>
  `).join('')
}

function renderMenuPdfPreview() {
  const strip = document.getElementById('menu-pdf-strip')
  if (!strip) return
  const menu = brandAssets.pdfMenus?.[0]
  if (!menu?.paths?.length) return
  strip.innerHTML = menu.paths.slice(0, 8).map((path, i) => `
    <a href="${path}" target="_blank" rel="noopener" class="menu-pdf-card" aria-label="Ver página ${i + 1} del menú 2025">
      <img src="${path}" alt="Menú La Capital 2025 página ${i + 1}" loading="lazy">
      <span>Página ${i + 1}</span>
    </a>
  `).join('')
}

function initMenuFilters() {
  document.querySelectorAll('.filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.filter-btn').forEach(b => {
        b.classList.remove('active')
        b.setAttribute('aria-selected', 'false')
      })
      btn.classList.add('active')
      btn.setAttribute('aria-selected', 'true')
      state.activeCategory = btn.dataset.category
      renderMenu(btn.dataset.category)
    })
  })
}

// ═══════════════════════════════════════════════════════════════
// CART
// ═══════════════════════════════════════════════════════════════

function addToCart(item) {
  const existing = state.cart.find(c => c.id === item.id)
  if (existing) {
    existing.qty++
  } else {
    state.cart.push({ ...item, qty: 1 })
  }
  saveCart()
  updateCartUI()
  showToast(`${item.name} añadido al carrito 🍔`)
}

function removeFromCart(id) {
  state.cart = state.cart.filter(c => c.id !== id)
  saveCart()
  updateCartUI()
}

function updateQty(id, delta) {
  const item = state.cart.find(c => c.id === id)
  if (!item) return
  item.qty += delta
  if (item.qty <= 0) {
    removeFromCart(id)
    return
  }
  saveCart()
  updateCartUI()
}

function saveCart() {
  localStorage.setItem('lc_cart', JSON.stringify(state.cart))
}

function getCartTotal() {
  return state.cart.reduce((sum, item) => sum + item.price * item.qty, 0)
}

function getCartCount() {
  return state.cart.reduce((sum, item) => sum + item.qty, 0)
}

function updateCartUI() {
  // Badge
  const badge = document.getElementById('cart-badge')
  const count = getCartCount()
  if (badge) {
    badge.textContent = count
    badge.dataset.count = count
    badge.style.display = count > 0 ? 'flex' : 'none'
  }

  // Items
  const itemsEl = document.getElementById('cart-items')
  const footerEl = document.getElementById('cart-footer')
  if (!itemsEl) return

  if (state.cart.length === 0) {
    itemsEl.innerHTML = `
      <div class="cart-empty">
        <span>🍔</span>
        <p>Tu carrito está vacío</p>
        <a href="#menu" class="btn btn--ghost btn--sm" onclick="document.getElementById('cart-drawer').classList.remove('open')">Explorar Carta</a>
      </div>
    `
    if (footerEl) footerEl.style.display = 'none'
    return
  }

  itemsEl.innerHTML = state.cart.map(item => `
    <div class="cart-item">
      <div class="cart-item-info">
        <div class="cart-item-name">${item.name}</div>
        <div class="cart-item-price">C$${(item.price * item.qty).toLocaleString()}</div>
      </div>
      <div class="cart-item-qty">
        <button onclick="window.__cartUpdate('${item.id}', -1)" aria-label="Reducir cantidad">−</button>
        <span>${item.qty}</span>
        <button onclick="window.__cartUpdate('${item.id}', 1)" aria-label="Aumentar cantidad">+</button>
      </div>
    </div>
  `).join('')

  const totalEl = document.getElementById('cart-total-amount')
  if (totalEl) totalEl.textContent = `C$${getCartTotal().toLocaleString()}`
  if (footerEl) footerEl.style.display = 'block'
}

// Expose cart functions globally
window.__cartUpdate = (id, delta) => updateQty(id, delta)

function initCart() {
  // Toggle
  const cartBtn = document.getElementById('cart-btn')
  const cartClose = document.getElementById('cart-close')
  const cartDrawer = document.getElementById('cart-drawer')
  const cartOverlay = cartDrawer?.querySelector('.cart-overlay')

  cartBtn?.addEventListener('click', () => toggleCart(true))
  cartClose?.addEventListener('click', () => toggleCart(false))
  cartOverlay?.addEventListener('click', () => toggleCart(false))

  // WhatsApp order
  document.getElementById('cart-whatsapp')?.addEventListener('click', () => {
    if (state.cart.length === 0) return
    const lines = state.cart.map(item => `${item.qty}x ${item.name} — C$${(item.price * item.qty).toLocaleString()}`)
    const total = getCartTotal()
    const msg = `🍔 *Pedido La Capital*\n\n${lines.join('\n')}\n\n*Total: C$${total.toLocaleString()}*\n\n¡Hola! Quiero hacer este pedido para recoger.`
    window.open(`https://wa.me/50583959307?text=${encodeURIComponent(msg)}`, '_blank')
  })

  updateCartUI()
}

function toggleCart(open) {
  const drawer = document.getElementById('cart-drawer')
  if (!drawer) return
  state.cartOpen = open
  drawer.classList.toggle('open', open)
  drawer.setAttribute('aria-hidden', !open)
  document.body.style.overflow = open ? 'hidden' : ''
}

// ═══════════════════════════════════════════════════════════════
// BUILDER
// ═══════════════════════════════════════════════════════════════

function initBuilder() {
  // Initialize 3D scene (lazy loaded)
  loadThreeModules().then(() => {
    if (burgerBuilderApi?.initBurgerBuilder) {
      burgerBuilderApi.initBurgerBuilder()
    }
  }).catch(e => console.log('3D builder fallback:', e.message))

  // Size buttons
  document.querySelectorAll('.size-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'))
      btn.classList.add('active')
      const sizeMap = { S: 'Sencilla', M: 'Doble', L: 'Triple' }
      setSize(sizeMap[btn.dataset.size] || btn.dataset.size)
    })
  })

  // Toppings
  document.querySelectorAll('.topping-chip').forEach(chip => {
    chip.addEventListener('click', (e) => {
      e.preventDefault()
      const input = chip.querySelector('input')
      const toppingMap = { cheddar: 'cheddar', bacon: 'tocino', mushrooms: 'cebolla caramelizada', jalapenos: 'pepinillos' }
      const added = toggleTopping(toppingMap[input.value] || input.value)
      input.checked = added
      chip.classList.toggle('selected', added)
    })
  })

  // Add to cart
  document.getElementById('builder-add')?.addEventListener('click', () => {
    const currentState = getState()
    const price = getBurgerPrice()
    const toppingNames = currentState.toppings.map(t => t.charAt(0).toUpperCase() + t.slice(1))
    const name = `Burger ${currentState.size}${toppingNames.length ? ' + ' + toppingNames.join(', ') : ''}`

    addToCart({
      id: `builder_${currentState.size}_${currentState.toppings.join('_') || 'plain'}`,
      name,
      price
    })
  })

  // Subscribe to state changes for price updates
  onBuilderChange((newState) => {
    const el = document.getElementById('builder-price')
    if (el) el.textContent = `C$${getBurgerPrice().toLocaleString()}`
  })
}

function getBuilderPrice() {
  let price = state.builderPrices[state.builderSize]
  state.builderToppings.forEach(t => { price += state.toppingPrices[t] || 0 })
  return price
}

function updateBuilderPrice() {
  const el = document.getElementById('builder-price')
  if (el) el.textContent = `C$${getBuilderPrice().toLocaleString()}`
}

// ═══════════════════════════════════════════════════════════════
// EVENTS
// ═══════════════════════════════════════════════════════════════

function renderEvents(tab = state.activeEventTab) {
  const list = document.getElementById('events-list')
  if (!list) return

  const filtered = getEventsByCategory(tab)
  list.innerHTML = filtered.map(event => `
    <article class="event-card${event.featured ? ' featured' : ''}">
      <div class="event-img" style="background-image: url('${event.image}')"></div>
      <div class="event-info">
        <span class="event-date">${event.dateFormatted}${event.time ? ` • ${event.time}` : ''}</span>
        <h3 class="event-title">${event.title}</h3>
        <p class="event-desc">${event.description}</p>
        <a href="${event.ctaUrl || '#reservar'}" class="btn btn--primary btn--sm">${event.ctaText || 'Reservar'}</a>
      </div>
    </article>
  `).join('')
}

function initEventTabs() {
  document.querySelectorAll('.events-tabs .tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.events-tabs .tab-btn').forEach(b => {
        b.classList.remove('active')
        b.setAttribute('aria-selected', 'false')
      })
      btn.classList.add('active')
      btn.setAttribute('aria-selected', 'true')
      state.activeEventTab = btn.dataset.tab
      renderEvents(btn.dataset.tab)
    })
  })
}

// ═══════════════════════════════════════════════════════════════
// TESTIMONIALS
// ═══════════════════════════════════════════════════════════════

function renderTestimonials() {
  const grid = document.getElementById('testimonials-grid')
  if (!grid) return

  grid.innerHTML = testimonials.map(t => `
    <article class="testimonial-card">
      <div class="testimonial-header">
        <div class="testimonial-avatar">${t.avatar}</div>
        <div>
          <div class="testimonial-name">${t.name}</div>
          <div class="testimonial-source">vía ${t.source}</div>
        </div>
      </div>
      <div class="testimonial-stars">${'★'.repeat(t.rating)}${'☆'.repeat(5 - t.rating)}</div>
      <p class="testimonial-text">"${t.text}"</p>
    </article>
  `).join('')
}

// ═══════════════════════════════════════════════════════════════
// FAQ
// ═══════════════════════════════════════════════════════════════

function renderFAQ() {
  const list = document.getElementById('faq-list')
  if (!list) return

  list.innerHTML = faq.map((item, i) => `
    <div class="faq-item" data-index="${i}">
      <button class="faq-question" aria-expanded="false">
        <span>${item.q}</span>
        <span class="faq-icon">+</span>
      </button>
      <div class="faq-answer" role="region">
        <div class="faq-answer-inner">${item.a}</div>
      </div>
    </div>
  `).join('')

  list.querySelectorAll('.faq-question').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.faq-item')
      const isOpen = item.classList.contains('open')

      // Close all
      list.querySelectorAll('.faq-item').forEach(i => {
        i.classList.remove('open')
        i.querySelector('.faq-question').setAttribute('aria-expanded', 'false')
      })

      // Toggle current
      if (!isOpen) {
        item.classList.add('open')
        btn.setAttribute('aria-expanded', 'true')
      }
    })
  })
}

// ═══════════════════════════════════════════════════════════════
// HEADER
// ═══════════════════════════════════════════════════════════════

function initHeader() {
  const header = document.getElementById('main-header')
  if (!header) return

  let ticking = false
  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        header.classList.toggle('scrolled', window.scrollY > 50)
        ticking = false
      })
      ticking = true
    }
  })

  // Active nav link on scroll
  const sections = document.querySelectorAll('section[id]')
  const navLinks = document.querySelectorAll('.nav-link')

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        navLinks.forEach(link => {
          link.classList.toggle('active', link.getAttribute('href') === `#${entry.target.id}`)
        })
      }
    })
  }, { rootMargin: '-30% 0px -70% 0px' })

  sections.forEach(section => observer.observe(section))
}

// ═══════════════════════════════════════════════════════════════
// MOBILE NAV
// ═══════════════════════════════════════════════════════════════

function initMobileNav() {
  const btn = document.getElementById('mobile-menu-btn')
  const nav = document.getElementById('mobile-nav')
  if (!btn || !nav) return

  btn.addEventListener('click', () => {
    state.mobileOpen = !state.mobileOpen
    btn.setAttribute('aria-expanded', state.mobileOpen)
    nav.classList.toggle('open', state.mobileOpen)
    nav.setAttribute('aria-hidden', !state.mobileOpen)
    document.body.style.overflow = state.mobileOpen ? 'hidden' : ''
  })

  // Close on link click
  nav.querySelectorAll('.mobile-link').forEach(link => {
    link.addEventListener('click', () => {
      state.mobileOpen = false
      btn.setAttribute('aria-expanded', 'false')
      nav.classList.remove('open')
      nav.setAttribute('aria-hidden', 'true')
      document.body.style.overflow = ''
    })
  })
}

// ═══════════════════════════════════════════════════════════════
// RESERVATION → WHATSAPP
// ═══════════════════════════════════════════════════════════════

function initReservationForm() {
  const form = document.getElementById('reservation-form')
  if (!form) return

  form.addEventListener('submit', (e) => {
    e.preventDefault()

    const data = new FormData(form)
    const name = data.get('name')?.trim()
    const phone = data.get('phone')?.trim()
    const date = data.get('date')
    const time = data.get('time')
    const guests = data.get('guests')
    const occasion = data.get('occasion')
    const notes = data.get('notes')?.trim()

    // Validate
    if (!name || !phone || !date || !time || !guests) {
      showToast('Por favor completa los campos obligatorios ⚠️')
      return
    }

    // Build message
    let msg = `🍽️ *Reserva La Capital*\n\n`
    msg += `👤 *Nombre:* ${name}\n`
    msg += `📞 *Teléfono:* ${phone}\n`
    msg += `📅 *Fecha:* ${date}\n`
    msg += `🕐 *Hora:* ${time}\n`
    msg += `👥 *Personas:* ${guests}\n`
    if (occasion) msg += `🎉 *Ocasión:* ${occasion}\n`
    if (notes) msg += `📝 *Notas:* ${notes}\n`
    msg += `\n¡Hola! Quiero confirmar esta reserva.`

    window.open(`https://wa.me/50583959307?text=${encodeURIComponent(msg)}`, '_blank')
    showToast('¡Reserva enviada por WhatsApp! 🎉')
    form.reset()
  })
}

function setMinDate() {
  const dateInput = document.getElementById('res-date')
  if (dateInput) {
    const today = new Date().toISOString().split('T')[0]
    dateInput.min = today
  }
}

// ═══════════════════════════════════════════════════════════════
// SMOOTH SCROLL
// ═══════════════════════════════════════════════════════════════

function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e) => {
      const target = document.querySelector(anchor.getAttribute('href'))
      if (!target) return
      e.preventDefault()
      const headerOffset = 80
      const elementPosition = target.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    })
  })
}

// ═══════════════════════════════════════════════════════════════
// TOAST
// ═══════════════════════════════════════════════════════════════

function showToast(message) {
  const container = document.getElementById('toast-container')
  if (!container) return

  const toast = document.createElement('div')
  toast.className = 'toast'
  toast.textContent = message
  container.appendChild(toast)

  setTimeout(() => {
    toast.classList.add('toast-out')
    setTimeout(() => toast.remove(), 300)
  }, 2500)
}

// ═══════════════════════════════════════════════════════════════
// GSAP ANIMATIONS (if available)
// ═══════════════════════════════════════════════════════════════

async function initAnimations() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  try {
    const gsap = (await import('gsap')).default
    const { ScrollTrigger } = await import('gsap/ScrollTrigger')
    gsap.registerPlugin(ScrollTrigger)

    // Hero entrance
    gsap.from('.hero-tag', { opacity: 0, y: 30, duration: 0.8, delay: 0.2 })
    gsap.from('.hero-title', { opacity: 0, y: 40, duration: 1, delay: 0.4 })
    gsap.from('.hero-desc', { opacity: 0, y: 30, duration: 0.8, delay: 0.6 })
    gsap.from('.hero-cta', { opacity: 0, y: 20, duration: 0.8, delay: 0.8 })
    gsap.from('.hero-stats', { opacity: 0, y: 20, duration: 0.8, delay: 1 })

    // Section reveals
    gsap.utils.toArray('.section-header').forEach(header => {
      gsap.from(header.children, {
        scrollTrigger: { trigger: header, start: 'top 80%' },
        opacity: 0, y: 30, stagger: 0.15, duration: 0.8
      })
    })

    // Cards stagger
    gsap.utils.toArray('.live-card, .testimonial-card').forEach(card => {
      gsap.from(card, {
        scrollTrigger: { trigger: card, start: 'top 85%' },
        opacity: 0, y: 30, duration: 0.6
      })
    })

    // History text
    gsap.from('.history-text p', {
      scrollTrigger: { trigger: '.history-text', start: 'top 70%' },
      opacity: 0, y: 20, stagger: 0.2, duration: 0.8
    })

  } catch (e) {
    // GSAP not available, graceful degradation
    console.log('GSAP animations not loaded:', e.message)
  }
}

// Init animations after DOM
requestAnimationFrame(() => initAnimations())
